import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type TPlacement = 'left' | 'right' | 'top' | 'bottom';

type TFloatingPanelProps = {
  children: (params: { isOpen: boolean }) => React.ReactNode;
  content:
    | React.ReactNode
    | ((params: { close: () => void }) => React.ReactNode);
  placement: TPlacement;
  offset?: number;
};

export const FloatingPanel: FC<TFloatingPanelProps> = ({
  children,
  content,
  placement = 'left',
  offset = 8,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [opened, setOpened] = useState(false);

  const [position, setPosition] = useState({
    top: 0,
    left: 0,
  });

  const close = () => setOpened(false);

  useLayoutEffect(() => {
    if (!opened) return;
    if (!triggerRef.current) return;
    if (!panelRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();

    const panelRect = panelRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'left':
        top = triggerRect.top;
        left = triggerRect.left - panelRect.width - offset;
        break;

      case 'right':
        top = triggerRect.top;
        left = triggerRect.right + offset;
        break;

      case 'top':
        top = triggerRect.top - panelRect.height - offset;
        left = triggerRect.left;
        break;

      case 'bottom':
        top = triggerRect.bottom + offset;
        left = triggerRect.left;
        break;

      default:
        break;
    }

    setPosition({ top, left });
  }, [opened, placement, offset]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }

      setOpened(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <>
      <div ref={triggerRef} onClick={() => setOpened((prev) => !prev)}>
        {children({ isOpen: opened })}
      </div>

      {opened &&
        createPortal(
          <div
            data-filter-interactive
            ref={panelRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 9999,
            }}
          >
            {typeof content === 'function' ? content({ close }) : content}
          </div>,
          document.body,
        )}
    </>
  );
};
